import { useMemo, useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import * as tradeInComponentSelectors from '@makemydeal/dr-dash-store/selectors/tradeInComponentSelectors';
import type { SelectInputOption } from '@interstate/components/SelectInput';
// components
import { SelectInput } from './TradeInSelectInput';
import { useSingleTradeInFormProvider } from '../MultiTradeIn/TradeInFormProvider';

// constants/enums
import { TRADE_IN_YEAR } from '@makemydeal/dr-dash-components';
import { MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';

interface YearsFieldProps {
    isManualFormOpen: boolean;
    disabled?: boolean;
}

const YearsField: React.FC<YearsFieldProps> = ({ isManualFormOpen, disabled }) => {
    const { tradeInIndex, tradeInValidator, tradeInFormErrors } = useSingleTradeInFormProvider();
    const dispatch = useDispatch();
    const vehicleYear = useSelector(tradeInsSelectors.getYear(tradeInIndex));
    const tradeInVehicleYears = useSelector(tradeInComponentSelectors.getTradeInComponentYears);
    const tradeInVehicle = useSelector(tradeInsSelectors.getTradeInComponentVehicle(tradeInIndex));
    const vinHasBeenDecoded = useSelector(tradeInsSelectors.vinHasBeenDecoded(tradeInIndex));
    const hasMultipleVin = useSelector(tradeInsSelectors.hasMultipleVin(tradeInIndex));

    const isValidYear = useMemo(() => {
        return vehicleYear && !isNaN(Number(vehicleYear)) && Number(vehicleYear) > 0;
    }, [vehicleYear]);

    useEffect(() => {
        if (vinHasBeenDecoded || isValidYear) {
            dispatch(tradeInsActionCreators.fetchTradeInComponentMakes(MANAGER_VIEW_APP_PREFIX, vehicleYear, tradeInIndex));
        }
    }, [vinHasBeenDecoded, dispatch, vehicleYear, isValidYear]);

    const yearField = Number(vehicleYear) === 0 ? '' : vehicleYear;
    const isInputDisabled = useMemo(() => {
        return hasMultipleVin || (tradeInVehicleYears.options.length === 0 && !yearField) || disabled;
    }, [hasMultipleVin, tradeInVehicleYears.options.length, yearField, disabled]);

    useLayoutEffect(() => {
        tradeInValidator.setValue('vehicle.year', yearField || '', { validate: true, clearErrors: false });
        tradeInValidator.commit(['vehicle.year'], { validate: true });
    }, [yearField, tradeInValidator]);

    const getTradeInYears = useMemo((): SelectInputOption[] => {
        const yearsOptions =
            yearField && !tradeInVehicleYears.options.includes(yearField)
                ? [...tradeInVehicleYears.options, yearField]
                : tradeInVehicleYears.options;

        return yearsOptions.map((year) => ({ label: year, value: year })).sort();
    }, [yearField, tradeInVehicleYears.options]);

    const onYearChange = (event: any) => {
        const value = event.target.value;
        const updatedVehicle = {
            ...tradeInVehicle,
            year: value,
            make: { id: '', name: '' },
            model: { id: '', name: '' },
            trim: { id: '', name: '', vehicleId: '' }
        };

        //tradeInValidator.setValues({ vehicle: { year: value, make: '', model: '', trim: '' } }, { mode: 'merge', validate: true });
        tradeInValidator.setValue('vehicle.year', value, { validate: true, clearErrors: false });
        tradeInValidator.setValue('vehicle.make', '', { validate: true, clearErrors: false });
        tradeInValidator.setValue('vehicle.model', '', { validate: true, clearErrors: false });
        tradeInValidator.setValue('vehicle.trim', '', { validate: true, clearErrors: false });
        tradeInValidator.commit(['vehicle.year', 'vehicle.make', 'vehicle.model', 'vehicle.trim'], { validate: true });

        dispatch(tradeInsActionCreators.updateTradeInYear(value, tradeInIndex));
        dispatch(tradeInsActionCreators.updateVehicle(updatedVehicle, tradeInIndex));
    };

    return (
        <>
            {isManualFormOpen && (
                <SelectInput
                    size="sm"
                    id="year-select-input"
                    data-testid="year-select-input"
                    label={TRADE_IN_YEAR}
                    name={TRADE_IN_YEAR}
                    onChange={onYearChange}
                    options={getTradeInYears}
                    value={yearField}
                    placeholder="YYYY"
                    disabled={isInputDisabled}
                    displayDeselectOption={false}
                    hasError={tradeInFormErrors.vehicle.year.hasError}
                    errorMessage={tradeInFormErrors.vehicle.year.errorMessage}
                />
            )}
        </>
    );
};

export default YearsField;
